import { all, takeEvery, call, put, select, delay } from 'redux-saga/effects';
import { defineMessages } from 'react-intl';
import { parse } from 'date-fns';
import _has from 'lodash/has';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';

import { PERMISSION, VENUE_SERVICE_TYPES } from 'constants/logic';
import { OPPORTUNITY_ACT_SOURCE } from 'constants/tracking';
import { trackAcceptedOpportunity, trackOpportunityAccepted } from 'helpers/events';
import { IA_KEY, isVenueDashboardSubtypeFoodAndBeverage } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import {
  venueOpportunities,
  venueSmartSpotOpportunities,
  venueSmartSpotBatchUpdate,
  venueSmartSpotSingleUpdate,
  opportunityRejectTracking,
} from 'store/saga/entities/bound';
import {
  selectActiveVenue,
  selectRouteQuery,
  selectUserHasPermission,
  selectIaLogic,
  selectSchedulesStartDate,
  selectVenueByVenueId,
} from 'selectors/general';
import { ACTIVE_VENUE_CHANGED } from 'features/App/App.saga';
import { doFetchVenueSmartspotData } from 'components/SmartSpot';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { reportError } from 'helpers/error-logging';
import { refreshVenueSchedulesCacheForVenueAndDate } from 'features/YieldManagement/Contexts/VenueSchedulesCache';
import {
  setOpportunityIsLoading,
  clearOpportunityIsLoading,
  setOpportunityIsSuccess,
  setHasInitialOpportunities,
} from './Opportunities.reducers';

const messages = {
  success: defineMessages({
    acceptAll: {
      id: 'opportunities.teaser-saga.success.accept-all',
      defaultMessage: 'Accepted all recommendations',
    },
    allocationApplied: {
      id: 'opportunities.teaser-saga.success.allocation-applied',
      defaultMessage: 'Network allocation applied for {currentTime}, {className} class.',
    },
  }),
  error: defineMessages({
    acceptAll: {
      id: 'opportunities.teaser-saga.error.accept-all',
      defaultMessage: 'There was a problem accepting all your opportunities',
    },
    allocationApplied: {
      id: 'opportunities.teaser-saga.error.allocation-applied',
      defaultMessage: 'There was a problem applying your network allocation for {currentTime}, {className} class.',
    },
  }),
};

export const ACT_ON_OPPORTUNITY = 'ACT_ON_OPPORTUNITY';
export const ACT_ON_OPPORTUNITIES = 'ACT_ON_OPPORTUNITIES';
export const ACT_ON_SMARTSPOT_OPPORTUNITIES = 'ACT_ON_SMARTSPOT_OPPORTUNITIES';
export const REJECT_OPPORTUNITY = 'REJECT_OPPORTUNITY';

export const actOnOpportunity = (
  venueId,
  opportunities,
  scheduleId,
  spotsAdded,
  className,
  startTime,
  opportunitySource,
  totalClasspassCapacity,
  isRecurring = false,
  viaEmail = false
) => ({
  type: ACT_ON_OPPORTUNITY,
  venueId,
  opportunities,
  scheduleId,
  spotsAdded,
  className,
  startTime,
  opportunitySource,
  totalClasspassCapacity,
  viaEmail,
  isRecurring,
});

export const actOnOpportunities = (venueId, opportunities, actedOnOpportunities) => ({
  type: ACT_ON_OPPORTUNITIES,
  venueId,
  opportunities,
  actedOnOpportunities,
});

export const actOnSmartSpotOpportunities = (venueId, smartSpotUpdates) => ({
  type: ACT_ON_SMARTSPOT_OPPORTUNITIES,
  venueId,
  smartSpotUpdates,
});

export const rejectOpportunity = (venueId, scheduleId, opportunityId) => ({
  type: REJECT_OPPORTUNITY,
  venueId,
  scheduleId,
  opportunityId,
});

export function* doFetchOpportunities(dependencies, action) {
  const { venueId, includeClassLevel = false } = action;
  const canSeeOpportunities = yield select(selectUserHasPermission, PERMISSION.DISPLAY_SPOT_MANAGEMENT);

  if (!canSeeOpportunities) return [];

  // Check if the specific venue is eligible for opportunities (not F&B/SKU)
  const venue = yield select(selectVenueByVenueId, venueId);
  const isFoodAndBeverage = isVenueDashboardSubtypeFoodAndBeverage(venue);

  // Don't fetch opportunities for F&B (SKU) venues
  if (isFoodAndBeverage) return [];

  const providesClassService = venue?.serviceTypes?.includes(VENUE_SERVICE_TYPES.CLASS);
  if (!providesClassService) return [];

  // Fetch both traditional and SmartSpot opportunities in parallel
  const [traditionalOpportunities, smartSpotOpportunities] = yield all([
    call(fetchEntityIfNeeded, venueOpportunities, [dependencies, { venueId, includeClassLevel }]),
    call(fetchEntityIfNeeded, venueSmartSpotOpportunities, [dependencies, { venueId }]),
  ]);

  // Combine both opportunity sources
  const allOpportunities = [...traditionalOpportunities, ...smartSpotOpportunities];
  yield put(setHasInitialOpportunities(allOpportunities.length > 0));
  return allOpportunities;
}

export function* doActOnTeaserOpportunity(dependencies, action) {
  const {
    venueId,
    opportunities,
    scheduleId,
    spotsAdded,
    className,
    startTime,
    opportunitySource,
    totalClasspassCapacity,
  } = action;
  const { intl } = dependencies;
  const currentTime = intl.formatTime(parse(startTime, 'HH:mm:ss', new Date()));
  const DELAY_LENGTH = 2000;

  try {
    yield put(setOpportunityIsLoading(scheduleId));

    // SmartSpot opportunities use different endpoint
    if (opportunitySource === 'SMARTSPOT') {
      const totalSpots = totalClasspassCapacity + spotsAdded;
      yield call(fetchEntity, venueSmartSpotSingleUpdate, [
        dependencies,
        { venueId, scheduleId, classpassSpots: totalSpots, ssEnabled: true, withAllFutures: false },
      ]);
    } else {
      // Traditional opportunities
      yield call(fetchEntity, venueOpportunities, [dependencies, { venueId, scheduleId, opportunities, spotsAdded }]);
    }

    yield put(setOpportunityIsSuccess(scheduleId, spotsAdded));
    yield call(trackAcceptedOpportunity, 'opportunity teaser', scheduleId, spotsAdded);
    yield delay(DELAY_LENGTH); // give the user a chance to see that the call succeeded

    // Refresh both traditional and SmartSpot opportunities
    yield all([
      call(fetchEntity, venueOpportunities, [dependencies, { venueId }]),
      call(fetchEntity, venueSmartSpotOpportunities, [dependencies, { venueId }]),
    ]);
  } catch (error) {
    yield all([
      put(clearOpportunityIsLoading(scheduleId)),
      put(createErrorBanner(messages.error.allocationApplied, { currentTime, className })),
      call(reportError, error),
    ]);
  }
}

export function* doActOnOpportunity(dependencies, action) {
  const { venueId, opportunities, scheduleId, spotsAdded, className, startTime, isRecurring, viaEmail } = action;
  const { intl } = dependencies;
  const currentTime = intl.formatTime(parse(startTime, 'HH:mm:ss', new Date()));
  const includeClassLevel = true;

  try {
    yield call(fetchEntity, venueOpportunities, [
      dependencies,
      { venueId, scheduleId, opportunities, spotsAdded, isRecurring, viaEmail, includeClassLevel },
    ]);
    yield put(createInfoBanner(messages.success.allocationApplied, { currentTime, className }));
    yield call(trackAcceptedOpportunity, 'opportunities', scheduleId, spotsAdded);
    yield call(trackOpportunityAccepted, { source: OPPORTUNITY_ACT_SOURCE.OPPORTUNITIES, scheduleId });
  } catch (error) {
    yield all([
      put(createErrorBanner(messages.error.allocationApplied, { currentTime, className })),
      call(reportError, error),
    ]);
  }
}

export function* doActOnOpportunities(dependencies, action) {
  const { venueId, opportunities, actedOnOpportunities } = action;
  try {
    yield call(fetchEntity, venueOpportunities, [dependencies, { venueId, opportunities, actedOnOpportunities }]);
    yield all(
      actedOnOpportunities.map(([scheduleId, spotsAdded]) =>
        call(trackAcceptedOpportunity, 'opportunities', scheduleId, spotsAdded)
      )
    );
    yield put(createInfoBanner(messages.success.acceptAll));
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.acceptAll)), call(reportError, error)]);
  }
}

export function* doActOnSmartSpotOpportunities(dependencies, action) {
  const { venueId, smartSpotUpdates } = action;
  const { intl } = dependencies;
  const isSingleUpdate = smartSpotUpdates.length === 1;

  try {
    if (isSingleUpdate) {
      const { scheduleId, spots, ssEnabled, withAllFutures } = smartSpotUpdates[0];

      yield call(fetchEntity, venueSmartSpotSingleUpdate, [
        dependencies,
        { venueId, scheduleId, classpassSpots: spots, ssEnabled, withAllFutures },
      ]);
    } else {
      const batchUpdates = smartSpotUpdates.map(({ scheduleId, spots, ssEnabled, withAllFutures }) => ({
        scheduleId,
        classpassSpots: spots,
        ssEnabled,
        withAllFutures,
      }));
      yield call(fetchEntity, venueSmartSpotBatchUpdate, [dependencies, { venueId, updates: batchUpdates }]);
    }

    yield all(
      smartSpotUpdates.map(({ scheduleId, spotsAdded }) =>
        call(trackAcceptedOpportunity, 'opportunities', scheduleId, spotsAdded)
      )
    );

    const startDate = yield select(selectSchedulesStartDate);
    yield all([
      call(fetchEntity, venueSmartSpotOpportunities, [dependencies, { venueId }]),
      call(fetchEntity, venueOpportunities, [dependencies, { venueId, includeClassLevel: true }]),
      call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDate }),
    ]);

    if (isSingleUpdate) {
      const { className, startTime } = smartSpotUpdates[0];
      const currentTime = intl.formatTime(parse(startTime, 'HH:mm:ss', new Date()));
      yield put(createInfoBanner(messages.success.allocationApplied, { currentTime, className }));
    } else {
      yield put(createInfoBanner(messages.success.acceptAll));
    }
  } catch (error) {
    const errorMessage = isSingleUpdate ? messages.error.allocationApplied : messages.error.acceptAll;
    const errorParams = isSingleUpdate
      ? {
          currentTime: intl.formatTime(parse(smartSpotUpdates[0].startTime, 'HH:mm:ss', new Date())),
          className: smartSpotUpdates[0].className,
        }
      : {};
    yield all([put(createErrorBanner(errorMessage, errorParams)), call(reportError, error)]);
  }
}

export function* doRejectOpportunity(dependencies, action) {
  const { venueId, opportunityId } = action;

  yield call(fetchEntity, opportunityRejectTracking, [dependencies, venueId, opportunityId]);
  // Refresh both opportunity sources
  yield all([
    call(fetchEntity, venueOpportunities, [dependencies, { venueId }]),
    call(fetchEntity, venueSmartSpotOpportunities, [dependencies, { venueId }]),
  ]);
}

export function* handleActiveVenueChange(dependencies, action) {
  yield call(dependencies.navigate, makePath.opportunities(action.venueId));
}

export function* handleRouteEnter(dependencies) {
  const { venueId } = yield select(selectActiveVenue);
  const { featureIsSupported } = yield select(selectIaLogic, IA_KEY.OPPORTUNITIES, venueId);
  const startDate = yield select(selectSchedulesStartDate);
  const query = yield select(selectRouteQuery);

  if (featureIsSupported) {
    const [, opportunities] = yield all([
      call(doFetchVenueSmartspotData, dependencies, { venueId, startDate }), // used for SS ad banner
      call(doFetchOpportunities, dependencies, { venueId, includeClassLevel: true }),
    ]);

    if (_has(query, 'spots') && _has(query, 'schedule_id')) {
      const spotsAdded = Number(query.spots);
      const scheduleId = Number(query.schedule_id);
      const opportunity = opportunities.find(record => record.scheduleId === scheduleId);
      if (opportunity) {
        const { className, startTime } = opportunity;
        const isRecurring = false;
        const viaEmail = true;
        yield put(
          actOnOpportunity(venueId, opportunities, scheduleId, spotsAdded, className, startTime, isRecurring, viaEmail)
        );
      }
    }
  }
}

export function* watchOpportunitiesActionsHome(dependencies) {
  yield takeEvery(ACT_ON_OPPORTUNITY, doActOnTeaserOpportunity, dependencies);
  yield takeEvery(REJECT_OPPORTUNITY, doRejectOpportunity, dependencies);
  yield takeEvery(ACTIVE_VENUE_CHANGED, doFetchOpportunities, dependencies);
}

export function* watchOpportunitiesActionsOpportunities(dependencies) {
  yield takeEvery(ACT_ON_OPPORTUNITY, doActOnOpportunity, dependencies);
  yield takeEvery(ACT_ON_OPPORTUNITIES, doActOnOpportunities, dependencies);
  yield takeEvery(ACT_ON_SMARTSPOT_OPPORTUNITIES, doActOnSmartSpotOpportunities, dependencies);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, dependencies);
}
